import sileo from 'sileo';
import axios from 'source/lib/axios';
import eventBus from 'source/lib/event-bus';

const Client = new sileo.Model('scalema_new', 'client-info');
const FollowUpQuestions = new sileo.Model('scalema_new', 'client-query-followup-questions');
const FollowUpQuestionChoices = new sileo.Model('scalema_new', 'followup-question-choices');
const CountryCodes = new sileo.Model('scalema_new', 'phone-country-codes');
const FollowUpQuestionResponse = new sileo.Model('scalema_new', 'followup-question-responses');
const ClientQuery = new sileo.Model('scalema_new', 'client-queries');
const ConversationMessage = new sileo.Model('scalema_new', 'conversation-message');
const MessageBlockRating = new sileo.Model('ai', 'message-block-rating');
const ScalemaAttachmentAmazonCredentials = new sileo.Model('scalema_new', 'scalema-attachment-amazon-form-data');
const ConversationTemplatePresetNodeIds = new sileo.Model ('ai', 'conversation-template-preset' );
const ClientCaptureAmazonFormDataResource = new sileo.Model ('scalema_new',
  'scalema-client-capture-csv-amazon-form-data');
const ScalemaBatchIntakeProcess = new sileo.Model ('scalema_new', 'scalema-batch-intake-process-resource');

function websocketOnScalemaMessageHandler(message) {
  let { payload } = JSON.parse(message.data.payload);
  if (payload.message?.includes('[email]')) {
    payload.message = payload.message.replace('[email]', '');
    eventBus.$emit('scalema-summary-streamed', payload, 'email');
  } else if (payload.message?.includes('[name]')) {
    payload.message = payload.message.replace('[name]', '');
    eventBus.$emit('scalema-summary-streamed', payload, 'name');
  } else if (payload.message?.includes('[phone]')) {
    payload.message = payload.message.replace('[phone]', '');
    eventBus.$emit('scalema-summary-streamed', payload, 'phone');
  } else if (payload.message?.includes('[website]')) {
    payload.message = payload.message.replace('[website]', '');
    eventBus.$emit('scalema-summary-streamed', payload, 'website');
  } else if (payload.message?.includes('[summary]')) {
    payload.message = payload.message.replace('[summary]', '');
    eventBus.$emit('scalema-summary-streamed', payload);
  } else {
    eventBus.$emit('scalema-message-streamed', payload);
  }
}

function initializeWorker() {
  return new Worker(new URL('./worker.js', import.meta.url));
}

const state = {
  webWorkerConnection: {}
};

const mutations = {
  terminateWebWorkerConnection(state) {
    const token = localStorage.getItem('scalema_token');
    if (state.webWorkerConnection[token]) {
      state.webWorkerConnection[token].postMessage({ action: 'terminateConnection' });
      state.webWorkerConnection[token].terminate();
      state.webWorkerConnection[token] = null;
    }
  },
};

const actions = {
    async initiateScalemaWebsocketConnection({ commit, state }, clientQueryId) {
      let token;
      const scalemaToken = localStorage.getItem('scalema_token');

      if (state.webWorkerConnection[token])
        return;

      if (scalemaToken && scalemaToken.endsWith(String(clientQueryId))) {
        token = scalemaToken;
      } else {
        let data = await axios.get('/websocket-scalema-token/?client_id=' + clientQueryId);
        token = `${data.token}${clientQueryId}`;
        localStorage.setItem('scalema_token', token);
      }
      if (!state.webWorkerConnection[token]) {
        state.webWorkerConnection[token] = initializeWorker();
        state.webWorkerConnection[token].postMessage({
          action: 'initiateConnection',
          payload: {
              token: token,

              API_URL: process.env.API_URL,
              /* eslint-disable */
          }
        });
      }


      state.webWorkerConnection[token].onmessage = function(event) {
        if (event.data.type === 'websocketMessage') {
            websocketOnScalemaMessageHandler(event);
        } else if (event.data.type === 'websocketClosed') {
          commit('terminateWebWorkerConnection');
        }
      };

      state.webWorkerConnection[token].onerror = function(event) {
        commit('terminateWebWorkerConnection');
        console.error('Worker Error:', event);
      };
    },

    createClientQuery(context, payload) {
      return ClientQuery.objects.create(payload);
    },

    createClient(context, payload) {
      return Client.objects.create(payload);
    },

    updateClientQuery(context, data) {
      const form = new FormData();
      for (let key in data) form.append(key, data[key]);
      return ClientQuery.objects.update({ 'pk': data.pk }, form);
    },

    editFollowUpQuestionResponse(context, data) {
      return FollowUpQuestionResponse.objects.update({}, data.form);
    },

    fetchMessageBlock(context, payload) {
      const scalema_token = localStorage.getItem('scalema_token');
      if (scalema_token) {
        payload['auth_token'] = localStorage.getItem('scalema_token');
      }
      const form = new FormData();
      Object.keys(payload).forEach((key) => {
        form.append(key, payload[key]);
      });
      return axios.post('scalema/message-block', form);
    },

    fetchConditionalBlockResponseMessage(context, payload) {
      const scalema_token = localStorage.getItem('scalema_token');
      if (scalema_token) {
        payload['auth_token'] = localStorage.getItem('scalema_token');
      }
      const form = new FormData();
      Object.keys(payload).forEach((key) => {
        form.append(key, payload[key]);
      });
      return axios.post('scalema/condition-block', form);
    },

    fetchAIResponse(context, payload) {
      const scalema_token = localStorage.getItem('scalema_token');
      if (scalema_token) {
        payload['auth_token'] = localStorage.getItem('scalema_token');
      }

      const form = new FormData();
      Object.keys(payload).forEach((key) => {
        form.append(key, payload[key]);
      });
      return axios.post('scalema/ai-completion', form);
    },

    fetchNewAISummary(context, payload) {
      const scalema_token = localStorage.getItem('scalema_token');
      if (scalema_token) {
        payload['auth_token'] = localStorage.getItem('scalema_token');
      }
      const form = new FormData();
      Object.keys(payload).forEach((key) => {
        form.append(key, payload[key]);
      });
      return axios.post('scalema/summary-scalema-ai', form);
    },

    fetchAIJobDescriptionSuggestion(context, { params, url }) {
      const scalema_token = localStorage.getItem('scalema_token');
      if (scalema_token) {
        params['websocket_token'] = scalema_token;
      }
      return axios.get(url, {
        params
      });
    },

    saveScalemaConversationMessage(context, data) {
      const form = new FormData();
      for (let key in data) form.append(key, data[key]);
      return ConversationMessage.objects.create(form);
    },

    saveScalemaRegeneratedMessage(context, data) {
      const form = new FormData();
      for (let key in data) form.append(key, data[key]);
      return ConversationMessage.objects.update({}, form);
    },

    fetchScalemaConversationMessage(context, filters) {
      return ConversationMessage.objects.filter(filters);
    },

    updateClientInfo(context, data) {
      return Client.objects.update({ 'pk': data.pk }, data.form);
    },

    fetchFollowUpQuestions(context) {
      return FollowUpQuestions.objects.filter();
    },

    fetchFollowUpQuestionChoices(context) {
      return FollowUpQuestionChoices.objects.filter();
    },

    fetchCountryCodes(context) {
      return CountryCodes.objects.filter();
    },

    fetchPresetNodeIds(context, filters){
      return ConversationTemplatePresetNodeIds.objects.filter(filters);
    },
    
    saveFollowUpQuestionResponse(context, payload) {
      return FollowUpQuestionResponse.objects.create(payload);
    },

    saveMessageBlockFeedback(context, data) {
      const form = new FormData();
      for (let key in data) form.append(key, data[key]);
      return MessageBlockRating.objects.update({ 'pk': data.pk }, form);
    },

    async scalemaFetchMessageAttachmentAmazonFormData(context, filters) {
      return await ScalemaAttachmentAmazonCredentials.objects.filter(filters).then(amazon => {
          return amazon.amazon;
      });
  },

    async handleClientCaptureAmazonFormDataResource(context, filters) {
      return await ClientCaptureAmazonFormDataResource.objects.filter(filters).then(data => {
          return data.amazon;
      });
  },

    createScalemaBatchIntakeProcess(context,data) {
      return ScalemaBatchIntakeProcess.objects.create(data);
    },
};

export default {
  state,
  mutations,
  namespaced: true,
  actions,
};
